import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Box, Chip, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import { CallReceived, CallMade, ExpandLess, ExpandMore, Phone } from "@mui/icons-material";
import { useStore } from "../../store/store";
import { LiveCall } from "../../store/store.types";
import { useUser } from "../../api/useAuth";
import { isLiveCallsSupervisor } from "../../utils/constants";

const HEADER_BACKGROUND = "#9e9e9e";

const sortLiveCalls = (calls: Array<LiveCall>) => {
    const next = [...calls];
    next.sort((a, b) => {
        if (a.state !== b.state) {
            if (a.state === "answered") return -1;
            if (b.state === "answered") return 1;
        }
        const aTime = new Date(a.startedAt).getTime();
        const bTime = new Date(b.startedAt).getTime();
        return bTime - aTime;
    });
    return next;
};

const buildElapsedLabel = (call: LiveCall, nowMs: number) => {
    const baseIso = call.state === "answered" && call.answeredAt ? call.answeredAt : call.startedAt;
    const baseMs = new Date(baseIso).getTime();
    const diffSeconds = Math.max(0, Math.floor((nowMs - baseMs) / 1000));
    const minutes = Math.floor(diffSeconds / 60);
    const seconds = diffSeconds % 60;
    const padded = `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    return padded;
};

const buildAgentLabel = (call: LiveCall) => {
    const firstName = call.agentUser?.firstName ?? null;
    const lastName = call.agentUser?.lastName ?? null;
    if (firstName && lastName) {
        const label = `${firstName} ${lastName}`;
        return label;
    }
    if (firstName) return firstName;
    if (lastName) return lastName;
    return "Sem agente";
};

const buildPrimaryLabel = (call: LiveCall) => {
    const label = call.callerName ?? call.phoneNumber ?? "Desconhecido";
    return label;
};

const buildCallerTypeChip = (call: LiveCall) => {
    if (call.callerCollection === "leads") {
        const chip = { label: "Lead", color: "info" as const };
        return chip;
    }
    if (call.callerCollection === "treatments") {
        const chip = { label: "Cliente", color: "success" as const };
        return chip;
    }
    const chip = { label: "Desconhecido", color: "default" as const };
    return chip;
};

const buildDeepLinkPath = (call: LiveCall) => {
    if (!call.callerCollection || !call.callerItemId) return null;
    if (call.callerCollection === "leads") return `/leads/${call.callerItemId}/detail`;
    if (call.callerCollection === "treatments") return `/treatments/${call.callerItemId}`;
    return null;
};

type LiveCallRowProps = {
    call: LiveCall;
    nowMs: number;
    onNavigate: (path: string) => void;
};

const LiveCallRow = ({ call, nowMs, onNavigate }: LiveCallRowProps) => {
    const directionIcon = call.direction === "in"
        ? <CallReceived sx={{ fontSize: 14, color: "#2e7d32" }} />
        : <CallMade sx={{ fontSize: 14, color: "#1976d2" }} />;
    const stateChip = call.state === "answered"
        ? { label: "Em chamada", color: "success" as const }
        : { label: "A tocar", color: "warning" as const };
    const deepLinkPath = buildDeepLinkPath(call);
    const elapsedLabel = buildElapsedLabel(call, nowMs);
    const agentLabel = buildAgentLabel(call);
    const primaryLabel = buildPrimaryLabel(call);
    const callerTypeChip = buildCallerTypeChip(call);
    const isClickable = deepLinkPath !== null;

    const handleClick = () => {
        if (deepLinkPath) {
            onNavigate(deepLinkPath);
        }
    };

    return (
        <Box
            onClick={handleClick}
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                px: 1.5,
                py: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
                cursor: isClickable ? "pointer" : "default",
                "&:hover": isClickable ? { backgroundColor: "action.hover" } : undefined,
                "&:last-of-type": { borderBottom: "none" },
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                {directionIcon}
                <Tooltip title={call.phoneNumber ?? ""} placement="top" arrow disableHoverListener={!call.phoneNumber}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
                        {primaryLabel}
                    </Typography>
                </Tooltip>
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
                    {elapsedLabel}
                </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                <Chip
                    label={callerTypeChip.label}
                    color={callerTypeChip.color}
                    variant="outlined"
                    size="small"
                    sx={{ height: 18, fontSize: 10 }}
                />
                <Chip
                    label={stateChip.label}
                    color={stateChip.color}
                    size="small"
                    sx={{ height: 18, fontSize: 10 }}
                />
            </Box>
            <Tooltip title={agentLabel} placement="top" arrow>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                    {agentLabel}
                </Typography>
            </Tooltip>
        </Box>
    );
};

export const LiveCallsDialog = () => {
    const { data: user } = useUser();
    const liveCalls = useStore((state) => state.liveCalls.bySessionId);
    const isMinimized = useStore((state) => state.liveCalls.isMinimized);
    const toggleMinimized = useStore((state) => state.liveCalls.toggleMinimized);
    const navigate = useNavigate();
    const [nowMs, setNowMs] = useState(() => Date.now());

    const sortedCalls = useMemo(() => {
        const calls = Object.values(liveCalls);
        const sorted = sortLiveCalls(calls);
        return sorted;
    }, [liveCalls]);

    useEffect(() => {
        if (sortedCalls.length === 0) return;
        if (isMinimized) return;
        const intervalId = setInterval(() => {
            setNowMs(Date.now());
        }, 1000);
        return () => clearInterval(intervalId);
    }, [sortedCalls.length, isMinimized]);

    const role = user?.role?.id;
    if (!isLiveCallsSupervisor(role)) return null;
    if (sortedCalls.length === 0) return null;

    if (isMinimized) {
        return (
            <Paper
                elevation={8}
                onClick={toggleMinimized}
                sx={{
                    position: "fixed",
                    bottom: 80,
                    left: 16,
                    borderRadius: 3,
                    overflow: "hidden",
                    zIndex: 1300,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    pl: 2,
                    pr: 2,
                    py: 1.25,
                    background: HEADER_BACKGROUND,
                    color: "white",
                    "&:hover": { transform: "scale(1.02)" },
                    transition: "transform 0.2s",
                }}
            >
                <Badge badgeContent={sortedCalls.length} color="error" overlap="circular">
                    <Phone fontSize="small" />
                </Badge>
                <Typography variant="body2" fontWeight={500} sx={{ ml: 0.5 }}>
                    Chamadas
                </Typography>
                <ExpandLess fontSize="small" />
            </Paper>
        );
    }

    return (
        <Paper
            elevation={8}
            sx={{
                position: "fixed",
                bottom: 80,
                left: 16,
                width: 220,
                maxHeight: "60vh",
                borderRadius: 3,
                overflow: "hidden",
                zIndex: 1300,
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Box
                onClick={toggleMinimized}
                sx={{
                    px: 1.5,
                    py: 1,
                    background: HEADER_BACKGROUND,
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                }}
            >
                <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>
                    Chamadas em curso · {sortedCalls.length}
                </Typography>
                <IconButton size="small" sx={{ color: "white", p: 0.25 }}>
                    <ExpandMore fontSize="small" />
                </IconButton>
            </Box>
            <Box sx={{ flex: 1, overflowY: "auto", p: "2px" }}>
                {sortedCalls.map((call) => (
                    <LiveCallRow key={call.id} call={call} nowMs={nowMs} onNavigate={navigate} />
                ))}
            </Box>
        </Paper>
    );
};
